import type { DocumentListQuery } from '@app/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createDocument,
  createDocumentValue,
  deleteDocument,
  deleteDocumentValue,
  fetchDocument,
  fetchDocumentValues,
  fetchDocuments,
  updateDocument,
  updateDocumentValue,
} from '../api/index.js';

export const KEY_DOCUMENTS = (extension?: string) =>
  ['documents', 'list', extension ?? 'all'] as const;
export const KEY_DOCUMENT = (id: string) => ['documents', 'detail', id] as const;
export const KEY_DOCUMENT_VALUES = (documentId: string) =>
  ['documents', 'values', documentId] as const;

export function useDocuments(params?: DocumentListQuery) {
  return useQuery({
    queryKey: KEY_DOCUMENTS(params?.extension),
    queryFn: () => fetchDocuments(params),
  });
}

export function useDocument(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEY_DOCUMENT(id),
    queryFn: () => fetchDocument(id),
    enabled: (options?.enabled ?? true) && Boolean(id),
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDocument,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', 'list'] });
      void qc.invalidateQueries({ queryKey: ['progression', 'site'] });
    },
  });
}

export function useUpdateDocument(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof updateDocument>[1]) => updateDocument(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_DOCUMENT(id) });
      void qc.invalidateQueries({ queryKey: ['documents', 'list'] });
      void qc.invalidateQueries({ queryKey: ['progression', 'site'] });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', 'list'] });
      void qc.invalidateQueries({ queryKey: ['progression', 'site'] });
    },
  });
}

export function useDocumentValues(documentId: string, enabled = true) {
  return useQuery({
    queryKey: KEY_DOCUMENT_VALUES(documentId),
    queryFn: () => fetchDocumentValues(documentId),
    enabled: enabled && Boolean(documentId),
  });
}

export function useCreateDocumentValue(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof createDocumentValue>[1]) =>
      createDocumentValue(documentId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_DOCUMENT_VALUES(documentId) });
      void qc.invalidateQueries({ queryKey: ['progression', 'site'] });
    },
  });
}

export function useUpdateDocumentValue(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      valueId,
      input,
    }: {
      valueId: string;
      input: Parameters<typeof updateDocumentValue>[2];
    }) => updateDocumentValue(documentId, valueId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_DOCUMENT_VALUES(documentId) });
      void qc.invalidateQueries({ queryKey: ['progression', 'site'] });
    },
  });
}

export function useDeleteDocumentValue(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (valueId: string) => deleteDocumentValue(documentId, valueId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_DOCUMENT_VALUES(documentId) });
      void qc.invalidateQueries({ queryKey: ['progression', 'site'] });
    },
  });
}
